/**
 * AppOnboardingPanel — Collapsed bottom strip (dumb component).
 *
 * Always visible at the bottom of the screen while onboarding is active.
 * Clicking anywhere on it (or the chevron) calls onToggle.
 */

import moment from "moment-timezone";
import { CommonImage } from "styledComponents/common";
import useCountdown from "../../customHooks/useCountdown";
import {
  BottomPanelWrapper,
  BadgeAndInfoGroup,
  ChevronButton,
  ChevronIcon,
  ContinueButton,
  CreditCopy,
  CreditHighlight,
  GiftIcon,
  PanelInner,
  PanelLeftGroup,
  PanelRightGroup,
  PanelStepInfo,
  PanelStepSubtitle,
  PanelStepTitle,
  StepBadge,
  StepBadgeNumber,
  StepBadgeTotal,
  TimerBox,
  TimerDivider,
  TimerValueWrapper,
  TimerValue,
  MobileMinimizedPanel,
  MobileMinimizedHeader,
  MobileSmallBadge,
  MobileTimerChip,
  MobileTrialBanner,
  MobileGiftIcon,
  MobileCreditText,
  MobileCollapseButton,
  MobileChevronIcon,
  MobileBadgeNumber,
  MobileBadgeTotal,
  TabletMinimizedPanel,
  TabletHeaderRow,
  TabletSmallBadge,
  TabletBadgeNumber,
  TabletBadgeTotal,
  TabletBanner,
  TabletGiftIcon,
  TabletDivider,
  TabletTimerChip,
  TabletCollapseButton,
} from "./shared/appOnboardingStyles";
import GiftCreditIcon from "../../assets/img/icons/gift.svg";
import ChevronIcon_SVG from "../../assets/img/icons/chevron.svg";
import { panelSlideUp } from "./shared/appOnboardingStyles";

const pad = n => String(n).padStart(2, "0");

const AppOnboardingPanel = ({
  completedCount,
  currentStepIndex,
  totalSteps,
  activeStepTitle,
  activeStepDescription,
  trialConfig,
  isExpanded,
  onToggle,
  isMobile,
  isTablet,
  selectedOrganization,
}) => {
  // Convert trialStartedAt from UTC to selected timezone or fallback to moment default timezone
  // Backend returns ISO 8601 format: "2026-03-30T11:35:35.922376" (assumed UTC)
  const convertedStartTime = trialConfig?.trialStartedAt
    ? selectedOrganization?.timezone
      ? moment.utc(trialConfig.trialStartedAt).tz(selectedOrganization.timezone)
      : moment(trialConfig.trialStartedAt)
    : null;

  const durationMs = trialConfig
    ? trialConfig.durationHours * 60 * 60 * 1000
    : null;

  const { hours, minutes, seconds, isExpired } = useCountdown({
    startTime: convertedStartTime?.format() ?? null,
    durationMs,
  });

  const showTimer = trialConfig && !isExpired;

  // Mobile collapsed panel: refactored with styled components
  if (isMobile) {
    return (
      <MobileMinimizedPanel {...panelSlideUp} onClick={onToggle}>
        <MobileMinimizedHeader>
          <MobileSmallBadge aria-label={`${completedCount} of ${totalSteps} steps completed`}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', left: 0, top: 0 }}>
              <circle cx="16" cy="16" r="16" fill="#EDF8FE" />
              <circle
                cx="16"
                cy="16"
                r="14"
                fill="none"
                stroke="#2266EF"
                strokeWidth="3"
                strokeDasharray={2 * Math.PI * 14}
                strokeDashoffset={2 * Math.PI * 14 * (1 - (completedCount / totalSteps))}
                strokeLinecap="round"
                transform="rotate(-90 16 16)"
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
            </svg>
            <MobileBadgeNumber>{completedCount}</MobileBadgeNumber>
            <MobileBadgeTotal>/{totalSteps}</MobileBadgeTotal>
          </MobileSmallBadge>

          {trialConfig && showTimer && (
            <MobileTimerChip>
              {showTimer && (
                <span>
                  {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                </span>
              )}
            </MobileTimerChip>
          )}
        </MobileMinimizedHeader>

        <MobileTrialBanner style={{ margin: 0}}>
          <MobileGiftIcon>
            <CommonImage src={GiftCreditIcon} alt="Gift" />
          </MobileGiftIcon>

          <MobileCreditText>
            Complete these steps in 24 hours to get a{" "}
            <CreditHighlight>
              {trialConfig?.creditCurrency ?? "$"}
              {trialConfig?.creditAmount ?? 5} credit
            </CreditHighlight>{" "}
            when upgrading
          </MobileCreditText>

          <MobileCollapseButton
            $isExpanded={isExpanded}
            aria-label={isExpanded ? "Collapse onboarding panel" : "Expand onboarding panel"}
            onClick={e => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <MobileChevronIcon as={CommonImage} src={ChevronIcon_SVG} alt="Chevron" />
          </MobileCollapseButton>
        </MobileTrialBanner>
      </MobileMinimizedPanel>
    );
  }

  // Tablet collapsed panel: refactored with styled components
  if (isTablet) {
    return (
      <TabletMinimizedPanel {...panelSlideUp} onClick={onToggle}>
        <TabletHeaderRow>
          <TabletSmallBadge aria-label={`${completedCount} of ${totalSteps} steps completed`}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', left: 0, top: 2 }}>
              <circle cx="30" cy="30" r="30" fill="#EDF8FE" />
              <circle
                cx="30"
                cy="30"
                r="27"
                fill="none"
                stroke="#2266EF"
                strokeWidth="5"
                strokeDasharray={2 * Math.PI * 27}
                strokeDashoffset={2 * Math.PI * 27 * (1 - (completedCount / totalSteps))}
                strokeLinecap="round"
                transform="rotate(-90 30 30)"
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
            </svg>
            <TabletBadgeNumber>{completedCount}</TabletBadgeNumber>
            <TabletBadgeTotal>/{totalSteps}</TabletBadgeTotal>
          </TabletSmallBadge>

          <TabletBanner style={{ visibility: !trialConfig || isExpired ? 'hidden' : 'visible' }}>
            <TabletGiftIcon>
              <CommonImage src={GiftCreditIcon} alt="Gift" />
            </TabletGiftIcon>

            <MobileCreditText>
              Complete these steps in 24 hours to get a{" "}
              <CreditHighlight>
                {trialConfig?.creditCurrency ?? "$"}
                {trialConfig?.creditAmount ?? 5} credit
              </CreditHighlight>{" "}
              when upgrading
            </MobileCreditText>

            <TabletDivider />

            <TabletTimerChip>
              {showTimer && (
                <span>
                  {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                </span>
              )}
            </TabletTimerChip>
          </TabletBanner>

          <TabletCollapseButton
            $isExpanded={isExpanded}
            aria-label={isExpanded ? "Collapse onboarding panel" : "Expand onboarding panel"}
            onClick={e => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <MobileChevronIcon as={CommonImage} src={ChevronIcon_SVG} alt="Chevron" />
          </TabletCollapseButton>
        </TabletHeaderRow>
      </TabletMinimizedPanel>
    );
  }

  // Desktop collapsed panel: original layout
  return (
    <BottomPanelWrapper {...panelSlideUp}>
      <PanelInner onClick={onToggle} role="button" aria-expanded={isExpanded}>
        {/* Left group: Badge + Step Info + Continue Button */}
        <PanelLeftGroup>
          {/* Badge and Info group */}
          <BadgeAndInfoGroup>
            {/* Step progress badge */}
            <StepBadge aria-label={`${completedCount} of ${totalSteps} steps completed`}>
              {/* SVG Progress Ring — driven by backend completedCount only */}
              <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', left: 0, top: 0 }}>
                {/* Background circle */}
                <circle cx="30" cy="30" r="30" fill="#EDF8FE" />

                {/* Progress ring - blue arc */}
                <circle
                  cx="30"
                  cy="30"
                  r="27"
                  fill="none"
                  stroke="#2266EF"
                  strokeWidth="5"
                  strokeDasharray={2 * Math.PI * 27}
                  strokeDashoffset={2 * Math.PI * 27 * (1 - (completedCount / totalSteps))}
                  strokeLinecap="round"
                  transform="rotate(-90 30 30)"
                  style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                />
              </svg>

              <StepBadgeNumber>{completedCount}</StepBadgeNumber>
              <StepBadgeTotal>/{totalSteps}</StepBadgeTotal>
            </StepBadge>

            {/* Step info */}
            <PanelStepInfo>
              <PanelStepTitle>{activeStepTitle}</PanelStepTitle>
              <PanelStepSubtitle>
                {currentStepIndex === 0
                  ? "Invite your technicians so they can receive jobs from the mobile app."
                  : "Continue setting up your account"}
              </PanelStepSubtitle>
            </PanelStepInfo>
          </BadgeAndInfoGroup>

          {/* Continue button */}
          <ContinueButton
            onClick={e => {
              e.stopPropagation();
              onToggle();
            }}
            type="button"
          >
            Continue
          </ContinueButton>
        </PanelLeftGroup>

        {/* Right group: Timer + Chevron */}
        <PanelRightGroup>
          {/* Trial timer + credit — always render but hide when expired */}
          <TimerBox style={{ visibility: !trialConfig || isExpired ? 'hidden' : 'visible' }}>
            {/* Gift icon */}
            <GiftIcon><CommonImage src={GiftCreditIcon} alt="Gift" /></GiftIcon>
            <CreditCopy>
              Complete these steps in 24 hours to get a{" "}
              <CreditHighlight>
                {trialConfig?.creditCurrency ?? "$"}
                {trialConfig?.creditAmount ?? 5} credit
              </CreditHighlight>
              {" "} when upgrading
            </CreditCopy>
            <TimerDivider />
            {showTimer && (
              <TimerValueWrapper>
                <TimerValue>
                  {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                </TimerValue>
              </TimerValueWrapper>
            )}
          </TimerBox>

          {/* Expand / collapse chevron */}
          <ChevronButton
            $isExpanded={isExpanded}
            aria-label={isExpanded ? "Collapse onboarding panel" : "Expand onboarding panel"}
            onClick={e => {
              e.stopPropagation(); // prevent double-toggle from PanelInner click
              onToggle();
            }}
          >
            <ChevronIcon as={CommonImage} src={ChevronIcon_SVG} alt="Chevron" />
          </ChevronButton>
        </PanelRightGroup>
      </PanelInner>
    </BottomPanelWrapper>
  );
};

export default AppOnboardingPanel;
